import AOS from "aos";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import ShuffleImg from "../../../assets/images/shuffle/shuffle-video.webp";
import FloatingBanner from "../../../components/FloatingBanner";
import { shuffleScheduleList } from "../../../data/schedule";
import {
  shuffleInstructorsList,
  shufflePricingList,
  shuffleVideoList,
} from "../../../data/shuffle";
import * as S from "./styled";

const Shuffle = () => {
  const scheduleRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const instructorsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [activeSection, setActiveSection] = useState<
    "schedule" | "pricing" | "video" | "instructors"
  >("schedule");

  const [scrollHeight, setScrollHeight] = useState<number>(180);

  const handleScroll = () => {
    if (
      wrapperRef.current &&
      scheduleRef.current &&
      pricingRef.current &&
      videoRef.current &&
      instructorsRef.current
    ) {
      const scheduleRect = scheduleRef.current.getBoundingClientRect();
      const pricingRect = pricingRef.current.getBoundingClientRect();
      const videoRect = videoRef.current.getBoundingClientRect();
      const instructorsRect = instructorsRef.current.getBoundingClientRect();
      const wrapperRect = wrapperRef.current.getBoundingClientRect();

      if (
        scheduleRect.top >= wrapperRect.top &&
        scheduleRect.bottom > scrollHeight
      ) {
        setActiveSection("schedule");
      } else if (
        pricingRect.top >= wrapperRect.top &&
        pricingRect.bottom > scrollHeight
      ) {
        setActiveSection("pricing");
      } else if (
        videoRect.top >= wrapperRect.top &&
        videoRect.bottom > scrollHeight
      ) {
        setActiveSection("video");
      } else {
        setActiveSection("instructors");
      }
    }
  };

  useEffect(() => {
    const updateScrollHeight = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth >= 1024) {
        setScrollHeight(180);
      } else if (screenWidth >= 768) {
        setScrollHeight(160);
      } else {
        setScrollHeight(120);
      }
    };

    updateScrollHeight();
    window.addEventListener("resize", updateScrollHeight);

    return () => {
      window.removeEventListener("resize", updateScrollHeight);
    };
  }, []);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const sectionPosition =
        ref.current.getBoundingClientRect().top + window.scrollY; // 섹션 위치 계산
      window.scrollTo({
        top: sectionPosition - scrollHeight + 5,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isReactSnap = navigator.userAgent === "ReactSnap";

    if (!isReactSnap) {
      AOS.init();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>넵튠 댄스 스튜디오 | 셔플 풋워크 전문 학원</title>

        <meta
          name="description"
          content="셔플 풋워크 전문 트레이닝 센터 | 기초부터 전문인 양성까지"
        />

        <meta
          name="keywords"
          content="넵튠, 넵튠 댄스, 넵튠 댄스 스튜디오, 셔플, 셔플댄스학원, 댄스자격증, 풋워크 수업, 국제실용댄스지도자협회, 국제실용댄스, 풋워크"
        />

        <meta property="og:title" content="넵튠 댄스 스튜디오" />
        <meta
          property="og:description"
          content="셔플 풋워크 전문 트레이닝 센터 | 기초부터 전문인 양성까지"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://neptunedance.com" />
        <meta
          property="og:image"
          content="https://neptunedance.com/og-image.png"
        />
      </Helmet>
      <S.ShuffleWrapper ref={wrapperRef}>
        <S.ShuffleWrap>
          <S.ShuffleClassNavList>
            <li
              onClick={() => {
                scrollToSection(scheduleRef);
              }}
              className={activeSection === "schedule" ? "active" : ""}
            >
              시간표
            </li>
            <li
              onClick={() => {
                scrollToSection(pricingRef);
              }}
              className={activeSection === "pricing" ? "active" : ""}
            >
              수강료
            </li>
            <li
              onClick={() => {
                scrollToSection(videoRef);
              }}
              className={activeSection === "video" ? "active" : ""}
            >
              수업영상
            </li>
            <li
              onClick={() => {
                scrollToSection(instructorsRef);
              }}
              className={activeSection === "instructors" ? "active" : ""}
            >
              강사진
            </li>
          </S.ShuffleClassNavList>
          <S.ShuffleTitleWrap>
            <div className="inner">
              <S.ShuffleTitleWrapContainer>
                <div
                  className="img-area"
                  data-aos="fade-up"
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  <img src={ShuffleImg} alt="mapImg" />
                </div>
                <div
                  className="text-area"
                  data-aos="fade-left"
                  data-aos-duration={700}
                  data-aos-easing="linear"
                >
                  <div className="title-text-wrap">
                    <h6>SHUFFLE</h6>
                    <p>셔플</p>
                  </div>
                  <div className="sub-text-wrap">
                    <p className="sub-text">
                      셔플댄스는 스텝댄스의 한 종류로
                      <br />
                      인터넷과 소셜 미디어를 통해 전 세계적으로{" "}
                      <br className="mobile" />
                      {"  "}인기를 얻은 춤입니다.
                    </p>
                    <p className="desc-text">
                      배우기 쉬운 것이 장점이며,주로 전자 음악에 맞춰 추던 초반
                      스타일에서
                      <br />
                      {"  "}각자 댄서들만의 스타일로 변형되기 시작하였으며
                      <br />
                      {"  "}음악에 구애 받지않고 각자의 색깔로 즐길 수 있는{" "}
                      <br className="mobile" />
                      하나의 춤 트렌드입니다.
                    </p>
                  </div>
                </div>
              </S.ShuffleTitleWrapContainer>
            </div>
          </S.ShuffleTitleWrap>
          <S.ScheduleWrap ref={scheduleRef}>
            <div className="inner">
              <S.ScheduleContainer>
                <div className="title-area">
                  <h6
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    SCHEDULE
                  </h6>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    셔플 수업 시간표
                  </p>
                </div>
                <div
                  data-aos="fade-left"
                  data-aos-delay={200}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  <ul>
                    {shuffleScheduleList.map((item) => {
                      return (
                        <S.ScheduleListItem key={item.dayKo}>
                          <div className="day-area">
                            <p>{item.dayEn}</p>
                            <p>{item.dayKo}</p>
                          </div>

                          <ul className="class-area">
                            {item.class.map((item) => (
                              <li>
                                <dl>
                                  <dt>{item.time}</dt>
                                  <dd>{item.title}</dd>
                                </dl>
                                <p>{item.instructor}</p>
                              </li>
                            ))}
                          </ul>
                        </S.ScheduleListItem>
                      );
                    })}
                  </ul>
                </div>
              </S.ScheduleContainer>
            </div>
          </S.ScheduleWrap>

          <S.PricingWrap ref={pricingRef}>
            <div className="inner">
              <S.PricingContainer>
                <div className="title-area">
                  <h6
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    PRICING
                  </h6>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    셔플 수강료
                  </p>
                </div>
                <S.ShufflePricingListWrap
                  data-aos="fade-left"
                  data-aos-delay={200}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  {shufflePricingList.map((pricing, index) => (
                    <>
                      <li key={pricing.type}>
                        <dl>
                          <dt className={`${index === 0 ? "grey" : "black"}`}>
                            {pricing.type}
                          </dt>
                          {pricing.class.map((classList, classListIndex) => (
                            <>
                              <div>
                                <div>
                                  <p>{classList.mainTitle}</p>
                                  <ul key={classList.mainTitle}>
                                    {classList.course.map((item) => (
                                      <li key={item.subTitle}>
                                        <div>
                                          <p>{item.subTitle}</p>
                                        </div>
                                        <span>{item.price}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                {index === 1 && classListIndex === 1 && (
                                  <p className="sub-text">
                                    4인이상 부터 1명당 3만원씩 추가 (10인 이상은
                                    상담신청)
                                  </p>
                                )}
                              </div>
                            </>
                          ))}
                        </dl>
                      </li>
                      {index === 0 && (
                        <>
                          <p>
                            *셔플 수업은 레벨별 수업, 정원이 빨리 마감되는
                            수업으로
                            <br />
                            4주차 월요일부터 순차적으로 카카오플러스친구로
                            등록이 가능하며 <br />
                            현재 수강 중인 회원님 먼저 재등록 후, 4주차부터 신규
                            회원님 등록이 <br className="mobile" />
                            가능한 점 양해 부탁드립니다. (중간에 등록불가)
                          </p>
                          <p>
                            *그 외의 장르 수업은 언제든지 수강 등록이 가능하며,
                            1회 쿠폰 수강도 가능합니다.
                          </p>
                        </>
                      )}
                    </>
                  ))}
                  <li className="mt-40">
                    <dl>
                      <dt className="black">전문인 양성과정</dt>
                      <div>
                        <div>
                          <p>입시 / 오디션 / 자격증반</p>
                          <ul>
                            <li>
                              <div>
                                <p>학원으로 문의</p>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </dl>
                  </li>
                </S.ShufflePricingListWrap>
              </S.PricingContainer>
            </div>
          </S.PricingWrap>
          <S.VideoWrap ref={videoRef}>
            <div className="inner">
              <S.VideoContainer>
                <div className="title-area">
                  <h6
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    VIDEO
                  </h6>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    셔플 수업 영상
                  </p>
                </div>
                <div
                  data-aos="fade-left"
                  data-aos-delay={200}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  <S.ShuffleVideoListWrap>
                    {shuffleVideoList.map((item, index) => (
                      <li key={index}>
                        <NavLink to={item.link} target="_blank">
                          <div>
                            <img src={item.img} alt="logo" />
                          </div>
                          <div className="text-wrap">
                            <p>{item.title}</p>
                            <span>{item.desc}</span>
                          </div>
                        </NavLink>
                      </li>
                    ))}
                  </S.ShuffleVideoListWrap>
                </div>
              </S.VideoContainer>
            </div>
          </S.VideoWrap>
          <S.InstructorsWrap ref={instructorsRef}>
            <div className="inner">
              <S.InstructorsContainer>
                <div className="title-area">
                  <h6
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    INSTRUCTORS
                  </h6>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    셔플 강사진
                  </p>
                </div>
                <div
                  data-aos="fade-up"
                  data-aos-delay={200}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  <S.ShuffleInstructorsListWrap>
                    {shuffleInstructorsList.map((instructor) => (
                      <li key={instructor.name}>
                        <NavLink to={`/instructors/${instructor.name}`}>
                          <i>
                            <img src={instructor.img} alt={instructor.name} />
                          </i>
                          <p>{instructor.name}</p>
                        </NavLink>
                      </li>
                    ))}
                  </S.ShuffleInstructorsListWrap>
                </div>
              </S.InstructorsContainer>
            </div>
          </S.InstructorsWrap>
        </S.ShuffleWrap>

        <FloatingBanner
          linkTo="http://pf.kakao.com/_xdxcJZn"
          text="수강 등록하기"
          target="_blank"
        />
      </S.ShuffleWrapper>
    </>
  );
};

export default Shuffle;
